import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useState } from "react";
import Image from "next/image";
import type { YoutubeOEmbedResponse } from "@/services/api/youtube";
import { fetchYoutubePreview } from "@/services/api/youtube";
import ChatItemHrefButtons from "@/components/waves/ChatItemHrefButtons";
import { useLinkPreviewContext } from "@/components/waves/LinkPreviewContext";
import { getYoutubeFetchUrl, parseYoutubeLink } from "./youtube";
const YOUTUBE_EMBED_HOSTS = new Map<string, string>([["youtube.com", "www.youtube.com"], ["www.youtube.com", "www.youtube.com"], ["youtube-nocookie.com", "www.youtube-nocookie.com"], ["www.youtube-nocookie.com", "www.youtube-nocookie.com"]]);
const ALLOWED_IFRAME_ALLOW_FEATURES = new Set(["accelerometer", "autoplay", "clipboard-write", "encrypted-media", "gyroscope", "picture-in-picture", "web-share"]);
const ALLOWED_IFRAME_LOADING_VALUES = new Set(["lazy", "eager"]);
const ALLOWED_IFRAME_REFERRER_POLICIES = new Set(["no-referrer", "no-referrer-when-downgrade", "origin", "origin-when-cross-origin", "same-origin", "strict-origin", "strict-origin-when-cross-origin", "unsafe-url"]);
const ALLOWED_EMBED_QUERY_PARAMS = new Set(["autoplay", "controls", "enablejsapi", "end", "feature", "index", "list", "loop", "modestbranding", "mute", "origin", "playsinline", "rel", "start"]);
const DEFAULT_IFRAME_STYLE = "width:100%;height:100%;";
const escapeHtmlAttribute = (value: string): string => value.replaceAll("&", "&amp;").replaceAll('"', "&quot;").replaceAll("<", "&lt;").replaceAll(">", "&gt;").replaceAll("'", "&#39;");
const sanitizeIframeAllow = (value: string | null): string | null => {
  if (!value) {
    return null;
  }
  const tokens = value.split(";").map(token => token.trim()).filter(Boolean).map(token => token.split(/\s+/)[0] ?? "");
  const allowedTokens: string[] = [];
  for (const token of tokens) {
    if (ALLOWED_IFRAME_ALLOW_FEATURES.has(token) && !allowedTokens.includes(token)) {
      allowedTokens.push(token);
    }
  }
  return allowedTokens.length > 0 ? allowedTokens.join("; ") : null;
};
const sanitizeIframeLoading = (value: string | null): string | null => {
  if (!value) {
    return null;
  }
  const normalized = value.trim().toLowerCase();
  return ALLOWED_IFRAME_LOADING_VALUES.has(normalized) ? normalized : null;
};
const sanitizeIframeReferrerPolicy = (value: string | null): string | null => {
  if (!value) {
    return null;
  }
  const normalized = value.trim().toLowerCase();
  return ALLOWED_IFRAME_REFERRER_POLICIES.has(normalized) ? normalized : null;
};
const sanitizeIframeFrameBorder = (value: string | null): string | null => {
  if (!value) {
    return null;
  }
  const normalized = value.trim();
  return normalized === "0" || normalized === "1" ? normalized : null;
};
const canonicalizeYoutubeEmbedUrl = (url: URL): URL | null => {
  if (url.protocol !== "https:") {
    return null;
  }
  const hostname = url.hostname.toLowerCase();
  const canonicalHost = YOUTUBE_EMBED_HOSTS.get(hostname);
  if (!canonicalHost) {
    return null;
  }
  const pathSegments = url.pathname.split("/").filter(Boolean);
  if (pathSegments.length !== 2 || pathSegments[0] !== "embed") {
    return null;
  }
  const videoId = pathSegments[1] ?? "";
  if (!/^[A-Za-z0-9_-]{6,}$/.test(videoId)) {
    return null;
  }
  const canonical = new URL(`https://${canonicalHost}/embed/${videoId}`);
  for (const [key, value] of url.searchParams.entries()) {
    if (ALLOWED_EMBED_QUERY_PARAMS.has(key)) {
      canonical.searchParams.set(key, value);
    }
  }
  return canonical;
};
const sanitizeYoutubeEmbedHtml = (html: string): string | null => {
  if (typeof globalThis === "undefined" || typeof DOMParser === "undefined") {
    return null;
  }
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const iframe = doc.querySelector("iframe");
  if (!iframe) {
    return null;
  }
  const src = iframe.getAttribute("src");
  if (!src) {
    return null;
  }
  let parsedUrl: URL;
  try {
    parsedUrl = new URL(src);
  } catch {
    return null;
  }
  const canonicalSrc = canonicalizeYoutubeEmbedUrl(parsedUrl);
  if (!canonicalSrc) {
    return null;
  }
  const attributes: string[] = [`src="${escapeHtmlAttribute(canonicalSrc.toString())}"`, `style="${DEFAULT_IFRAME_STYLE}"`];
  const title = iframe.getAttribute("title");
  if (title) {
    attributes.push(`title="${escapeHtmlAttribute(title)}"`);
  }
  const allow = sanitizeIframeAllow(iframe.getAttribute("allow"));
  if (allow) {
    attributes.push(`allow="${escapeHtmlAttribute(allow)}"`);
  }
  if (iframe.hasAttribute("allowfullscreen")) {
    attributes.push("allowfullscreen");
  }
  const loading = sanitizeIframeLoading(iframe.getAttribute("loading"));
  if (loading) {
    attributes.push(`loading="${loading}"`);
  }
  const referrerPolicy = sanitizeIframeReferrerPolicy(iframe.getAttribute("referrerpolicy"));
  if (referrerPolicy) {
    attributes.push(`referrerpolicy="${referrerPolicy}"`);
  }
  const frameBorder = sanitizeIframeFrameBorder(iframe.getAttribute("frameborder"));
  if (frameBorder) {
    attributes.push(`frameborder="${frameBorder}"`);
  }
  return `<iframe ${attributes.join(" ")}></iframe>`;
};
interface YoutubePreviewProps {
  readonly href: string;
}
type PreviewState = {
  readonly href: string;
  readonly preview: YoutubeOEmbedResponse | null;
  readonly hasError: boolean;
  readonly showEmbed: boolean;
};
const YoutubePreview = t0 => {
  const $ = _c(39);
  const {
    href
  } = t0;
  let t1;
  if ($[0] !== href) {
    t1 = parseYoutubeLink(href);
    $[0] = href;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const linkInfo = t1;
  if (!linkInfo) {
    throw new Error("Invalid YouTube link");
  }
  const {
    videoId
  } = linkInfo;
  const {
    hideActions
  } = useLinkPreviewContext();
  let t2;
  if ($[2] !== href) {
    t2 = () => ({
      href,
      preview: null,
      hasError: false,
      showEmbed: false
    });
    $[2] = href;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [state, setState] = useState(t2);
  const isCurrent = state.href === href;
  const preview = isCurrent ? state.preview : null;
  const hasError = isCurrent ? state.hasError : false;
  const showEmbed = isCurrent ? state.showEmbed : false;
  let t3;
  if ($[4] !== href) {
    t3 = () => {
      setState(prev => prev.href === href ? {
        ...prev,
        showEmbed: true
      } : {
        href,
        preview: null,
        hasError: false,
        showEmbed: true
      });
    };
    $[4] = href;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const handleShowEmbed = t3;
  let t4;
  let t5;
  if ($[6] !== href || $[7] !== videoId) {
    t4 = () => {
      const abortController = new AbortController();
      let isActive = true;
      const fetchUrl = getYoutubeFetchUrl(href, videoId);
      const loadPreview = async () => {
        ;
        try {
          const data = await fetchYoutubePreview(fetchUrl, abortController.signal);
          if (!isActive) {
            return;
          }
          if (data) {
            const sanitizedHtml = sanitizeYoutubeEmbedHtml(data.html);
            if (!sanitizedHtml) {
              setState(prev_1 => prev_1.href === href ? {
                ...prev_1,
                preview: null,
                hasError: true,
                showEmbed: false
              } : {
                href,
                preview: null,
                hasError: true,
                showEmbed: false
              });
              return;
            }
            const normalizedPreview = {
              ...data,
              html: sanitizedHtml
            };
            setState(prev_2 => prev_2.href === href ? {
              ...prev_2,
              preview: normalizedPreview,
              hasError: false
            } : {
              href,
              preview: normalizedPreview,
              hasError: false,
              showEmbed: false
            });
            return;
          }
          setState(prev_3 => prev_3.href === href ? {
            ...prev_3,
            preview: null,
            hasError: true,
            showEmbed: false
          } : {
            href,
            preview: null,
            hasError: true,
            showEmbed: false
          });
        } catch (t6) {
          const error = t6;
          if (!isActive) {
            return;
          }
          if (error instanceof DOMException && error.name === "AbortError") {
            return;
          }
          setState(prev_0 => prev_0.href === href ? {
            ...prev_0,
            preview: null,
            hasError: true,
            showEmbed: false
          } : {
            href,
            preview: null,
            hasError: true,
            showEmbed: false
          });
        }
      };
      loadPreview();
      return () => {
        isActive = false;
        abortController.abort();
      };
    };
    t5 = [href, videoId];
    $[6] = href;
    $[7] = videoId;
    $[8] = t4;
    $[9] = t5;
  } else {
    t4 = $[8];
    t5 = $[9];
  }
  useEffect(t4, t5);
  if (hasError) {
    throw new Error("Failed to load YouTube preview");
  }
  if (!preview) {
    let t6;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <div className="tw-min-w-0 tw-flex-1"><div className="tw-aspect-video tw-w-full tw-animate-pulse tw-rounded-lg tw-bg-iron-800" /></div>;
      $[10] = t6;
    } else {
      t6 = $[10];
    }
    let t7;
    if ($[11] !== hideActions || $[12] !== href) {
      t7 = !hideActions && <ChatItemHrefButtons href={href} />;
      $[11] = hideActions;
      $[12] = href;
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    let t8;
    if ($[14] !== t7) {
      t8 = <div className="tw-flex tw-w-full tw-items-stretch tw-gap-x-1">{t6}{t7}</div>;
      $[14] = t7;
      $[15] = t8;
    } else {
      t8 = $[15];
    }
    return t8;
  }
  const ariaLabel = preview.title ? `Play YouTube video ${preview.title}` : `Play YouTube video ${videoId}`;
  let t6;
  if ($[16] !== ariaLabel || $[17] !== handleShowEmbed || $[18] !== preview.html || $[19] !== preview.thumbnail_url || $[20] !== preview.title || $[21] !== showEmbed) {
    t6 = <div className="tw-relative tw-overflow-hidden tw-rounded-lg tw-bg-black">{showEmbed ? <div className="tw-relative tw-aspect-video tw-w-full tw-bg-black" data-testid="youtube-embed" dangerouslySetInnerHTML={{
        __html: preview.html
      }} /> : <button type="button" className="tw-relative tw-aspect-video tw-w-full tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0" onClick={handleShowEmbed} aria-label={ariaLabel}><Image src={preview.thumbnail_url} alt={preview.title ? `YouTube thumbnail for ${preview.title}` : "YouTube video thumbnail"} fill={true} sizes="100vw" className="tw-object-cover" /><div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-black/40"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="tw-h-12 tw-w-12 tw-text-white tw-opacity-90" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg></div></button>}</div>;
    $[16] = ariaLabel;
    $[17] = handleShowEmbed;
    $[18] = preview.html;
    $[19] = preview.thumbnail_url;
    $[20] = preview.title;
    $[21] = showEmbed;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== preview.title) {
    t7 = preview.title && <p className="tw-mb-0 tw-text-sm tw-font-semibold tw-text-iron-100">{preview.title}</p>;
    $[23] = preview.title;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== preview.author_name) {
    t8 = preview.author_name && <p className="tw-mb-0 tw-text-xs tw-text-iron-400">{preview.author_name}</p>;
    $[25] = preview.author_name;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== t7 || $[28] !== t8) {
    t9 = <div className="tw-mt-2 tw-space-y-1">{t7}{t8}</div>;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== t6 || $[31] !== t9) {
    t10 = <div className="tw-min-w-0 tw-flex-1">{t6}{t9}</div>;
    $[30] = t6;
    $[31] = t9;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== hideActions || $[34] !== href) {
    t11 = !hideActions && <ChatItemHrefButtons href={href} />;
    $[33] = hideActions;
    $[34] = href;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] !== t10 || $[37] !== t11) {
    t12 = <div className="tw-flex tw-w-full tw-items-stretch tw-gap-x-1">{t10}{t11}</div>;
    $[36] = t10;
    $[37] = t11;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  return t12;
};
export default YoutubePreview;